import { gql } from "@apollo/client";
import { isPermissionNotGrantted } from "context/ProjectProvider";
import { getLoggedinUserId } from "services/auth";
import { matchUserProjectRole } from "utils/utils";
import { isCurrentProjectArchived } from "utils/project-archive-state";
import {
  ESubmittalStatus,
  EUserTypes,
  isGcProjectAdminRole,
  ProjectPermissionEnum
} from "../../constants";

const GC_ADMIN_GENERAL_INFO_FIELDS = [
  "title",
  "custom_spec_section",
  "spec_section",
  "type",
  "description",
  "priority",
  "watchers",
  "float",
  "submittal_number"
];

const GC_CREATE_FIELDS = [
  "title",
  "spec_section",
  "type",
  "issue_date",
  "due_date",
  "ROJ",
  "responsible_contractor",
  "gc_reviewer",
  "design_reviewer",
  "submitter",
  "assignee",
  "gc_representative",
  "description",
  "comment",
  "attachment",
  "assign",
  "review_status",
  "submittal_manager",
  "received_from",
  "custom_spec_section",
  "priority",
  "watchers",
  "float",
  "submittal_number"
];

const GC_REVIEW_ACTION_FIELDS = [
  "comment",
  "attachment",
  "assign",
  "review_status"
];
const GC_ASSIGNED_ACTION_FIELDS = ["comment", "attachment"];
const DESIGN_REVIEWER_FIELDS = [
  "comment",
  "attachment",
  "assign",
  "review_status"
];
const SC_SUBMITTER_FIELDS = ["comment", "attachment", "assign"];
const SC_ATTACHMENT_FIELDS = ["attachment", "comment"];

const isSubmittalEditingBlocked = (
  updatedData: any,
  submittal?: any
): boolean =>
  updatedData?.workflow_status === ESubmittalStatus.VOID ||
  submittal?.workflow_status === ESubmittalStatus.VOID ||
  !!updatedData?.deleted_from_source;

const isAssignedToSelf = (
  updatedData: any,
  submittalParticipants: any,
  userId: string
): boolean =>
  (updatedData?.assignee && updatedData?.assignee === userId) ||
  submittalParticipants?.assignee?.user?.id === userId;

const isGcReviewer = (
  updatedData: any,
  submittalParticipants: any,
  userId: string
): boolean =>
  updatedData?.gc_reviewer === userId ||
  submittalParticipants?.gcReviewer?.user?.id === userId;

const isSubmitter = (
  updatedData: any,
  submittalParticipants: any,
  userId: string
): boolean =>
  updatedData?.submitter === userId ||
  submittalParticipants?.submitter?.user?.id === userId;

const isGcProjectAdmin = (tokenContents: any): boolean =>
  isGcProjectAdminRole(tokenContents?.role);

const getGcAdminGeneralInfoFields = (tokenContents: any): string[] =>
  isGcProjectAdmin(tokenContents) ? GC_ADMIN_GENERAL_INFO_FIELDS : [];

const getGcEditableFields = (
  submittalViewState: { status: number },
  updatedData: any,
  tokenContents: any,
  submittalParticipants: any,
  userId: string,
  assignedToSelf: boolean
): string[] => {
  const generalInfoFields = getGcAdminGeneralInfoFields(tokenContents);

  if (submittalViewState.status === (ESubmittalStatus.CREATE as number)) {
    return GC_CREATE_FIELDS;
  }

  if (
    assignedToSelf &&
    submittalViewState.status === (ESubmittalStatus.REVIEW as number) &&
    isGcReviewer(updatedData, submittalParticipants, userId)
  ) {
    return [...GC_REVIEW_ACTION_FIELDS, ...generalInfoFields];
  }

  if (assignedToSelf) {
    return [...GC_ASSIGNED_ACTION_FIELDS, ...generalInfoFields];
  }

  if (isGcProjectAdmin(tokenContents)) {
    return generalInfoFields;
  }

  return [];
};

const getScEditableFields = (
  submittalViewState: { status: number },
  updatedData: any,
  submittalParticipants: any,
  userId: string,
  assignedToSelf: boolean
): string[] => {
  if (submittalViewState.status !== (ESubmittalStatus.SUBMIT as number)) {
    return [];
  }

  if (
    assignedToSelf &&
    isSubmitter(updatedData, submittalParticipants, userId)
  ) {
    return SC_SUBMITTER_FIELDS;
  }

  if (
    assignedToSelf &&
    (updatedData?.submitter !== userId ||
      // unreachable: reaching here requires isSubmitter() to be false, which forces updatedData.submitter !== userId to be true, so the || short-circuits before this operand
      /* v8 ignore start */
      submittalParticipants?.submitter?.user?.id === userId)
    /* v8 ignore stop */
  ) {
    return SC_ATTACHMENT_FIELDS;
  }

  return [];
};

const getOwnerEditableFields = (
  submittalViewState: { status: number },
  updatedData: any,
  submittalParticipants: any,
  userId: string,
  assignedToSelf: boolean
): string[] => {
  if (
    assignedToSelf &&
    isSubmitter(updatedData, submittalParticipants, userId) &&
    submittalViewState.status === (ESubmittalStatus.SUBMIT as number)
  ) {
    return SC_SUBMITTER_FIELDS;
  }

  return [];
};

export const getEditableFields = (
  submittalViewState: {
    status: number;
    userType: unknown;
  },
  updatedData: any,
  tokenContents: any,
  submittal?: any,
  submittalParticipants?: any
) => {
  if (
    isCurrentProjectArchived() ||
    !updatedData ||
    isSubmittalEditingBlocked(updatedData, submittal)
  ) {
    return [];
  }

  const userId = getLoggedinUserId();
  const role = tokenContents?.role;
  const assignedToSelf = isAssignedToSelf(
    updatedData,
    submittalParticipants,
    userId
  );

  if (submittalViewState.status === (ESubmittalStatus.DONE as number)) {
    return [];
  }

  if (matchUserProjectRole(EUserTypes.GENERAL_CONTRACTOR, role)) {
    return getGcEditableFields(
      submittalViewState,
      updatedData,
      tokenContents,
      submittalParticipants,
      userId,
      assignedToSelf
    );
  }

  if (matchUserProjectRole(EUserTypes.SUBCONTRACTOR, role)) {
    return getScEditableFields(
      submittalViewState,
      updatedData,
      submittalParticipants,
      userId,
      assignedToSelf
    );
  }

  if (matchUserProjectRole(EUserTypes.ARCHITECT, role)) {
    return assignedToSelf ? DESIGN_REVIEWER_FIELDS : [];
  }

  if (matchUserProjectRole(EUserTypes.ENGINEER, role)) {
    return assignedToSelf ? DESIGN_REVIEWER_FIELDS : [];
  }

  if (matchUserProjectRole(EUserTypes.OWNER, role)) {
    return getOwnerEditableFields(
      submittalViewState,
      updatedData,
      submittalParticipants,
      userId,
      assignedToSelf
    );
  }

  return [];
};

export const agGridCheckFieldEditable = (params: any) => {
  if (
    isPermissionNotGrantted(
      ProjectPermissionEnum.InlineEditSubmittal,
      params.context.tokenContents?.role
    )
  )
    return false;

  let submittalStatus = ESubmittalStatus.CREATE;
  if (params.data.history?.[0]) {
    submittalStatus = params.data.history[0].status;
  }

  submittalStatus = submittalStatus || ESubmittalStatus.SUBMIT;

  const editableFields = getEditableFields(
    {
      userType: params.context.tokenContents?.userType,
      status: submittalStatus
    },
    params.data,
    params.context.tokenContents
  );
  if (editableFields.includes(params.colDef.colId)) return true;
  return false;
};

export const updateSubmittalOne = async (
  variables: any,
  customGQLClient: any
) => {
  let keysString = "";
  Object.keys(variables).forEach((key: any) => {
    keysString += `${key}: $${key}
    `;
  });
  const mutationStr = `mutation MyMutation($assignee: String = "", $assign_to: String = "", $assignee_unregistered: String = "", $comment: String = "", $deleted: Boolean = false, $description: String = "", $design_reviewer: String = "", $design_reviewer_unregistered: String = "", $design_reviewer_vendor_unregistered: String = "", $due_date: String = "", $gc_reviewer: String = "", $governing_date: String = "", $issue_date: String = "", $on_behalf_of: String = "", $on_behalf_of_unregistered: String = "", $on_behalf_of_vendor: String = "", $para: String = "", $received_from: String = "", $responsible_contractor: String = "", $responsible_contractor_unregistered: String = "", $revision: Int = 10, $ROJ: String = "", $spec_name: String = "", $spec_no: String = "", $spec_section_id: String = "", $status: Int = 10, $sub_section_heading: String = "", $submittal_id: String = "", $submittal_manager: String = "", $submittal_response_id: Int = 10, $submitter: String = "", $submitter_unregistered: String = "", $title: String = "", $type: Int = 10, $actual_milestone_date: String = "") {
    update_submittal_one(
        ${keysString}
    ) {
      message
    }
  }
  `;

  const query = gql(mutationStr);
  let result: any;
  try {
    result = await customGQLClient.mutate({
      mutation: query,
      variables
    });
  } catch (ex: any) {
    console.error("update_submittal_one ", ex);
    return ex;
  }
  return result;
};
